import { useState } from "react";
import { Check, CheckCircle2, Sparkles } from "lucide-react";
import { FadeIn, Eyebrow } from "./Reveal";

const PARTNER_POINTS = [
  "No joining fee",
  "No investment",
  "No targets",
  "Unlimited earning potential",
  "Just recommend a useful platform",
];

const STUDENT_POINTS = [
  "Useful and reliable updates",
  "Stay ahead of deadlines",
  "Access to important opportunities",
  "Better academic planning",
];

const TABLE_ROWS = [
  { referrals: "50 referrals/month", earnings: "₹7,500" },
  { referrals: "100 referrals/month", earnings: "₹15,000" },
  { referrals: "500 referrals/month", earnings: "₹75,000", highlight: true },
  { referrals: "1000 referrals/month", earnings: "₹1,50,000" },
];

export default function EarningSection() {
  const [referrals, setReferrals] = useState(50);
  const earnings = referrals * 150;
  const fill = ((referrals - 10) / (1000 - 10)) * 100;

  return (
    <section id="earning" data-testid="earning-section" className="py-16 sm:py-24 lg:py-32">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <Eyebrow index="01" label="Earning Potential" />
          <h2 className="font-display mt-4 text-3xl font-black tracking-tight text-[#1a237e] sm:text-4xl lg:text-5xl">
            Your Earning Potential
          </h2>
        </FadeIn>

        <div className="mt-12 grid gap-6 md:grid-cols-5">
          <FadeIn className="md:col-span-3" testid="partner-earning-card">
            <div className="card-hover relative h-full overflow-hidden rounded-3xl bg-[#1a237e] p-7 text-white sm:p-9">
              <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#283593]" />
              <p className="font-mono-edu mb-4 text-xs font-bold uppercase tracking-[0.2em] text-[#f57c00]">
                For You
              </p>
              <h3 className="font-display relative text-2xl font-extrabold leading-snug sm:text-3xl">
                Earn ₹150 for every Premium subscriber you refer!
              </h3>
              <ul className="mt-7 grid gap-3 sm:grid-cols-2 sm:gap-x-8">
                {PARTNER_POINTS.map((point) => (
                  <li
                    key={point}
                    className="flex items-center gap-3 text-sm font-medium text-white/90 sm:text-base"
                  >
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-[#f57c00]" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>

          <FadeIn className="md:col-span-2" delay={0.1} testid="student-cashback-card">
            <div className="card-hover h-full rounded-3xl border border-[#e2e8f0] bg-white p-7 sm:p-8">
              <p className="font-mono-edu mb-4 text-xs font-bold uppercase tracking-[0.2em] text-[#f57c00]">
                For Your Students
              </p>
              <h3 className="font-display text-xl font-extrabold leading-snug text-[#1a237e] sm:text-2xl">
                Students Get ₹50 Wallet Cashback
              </h3>
              <ul className="mt-6 grid gap-3">
                {STUDENT_POINTS.map((point) => (
                  <li
                    key={point}
                    className="flex items-center gap-3 text-sm font-medium text-slate-700"
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#fff3e0]">
                      <Check className="h-3.5 w-3.5 text-[#f57c00]" />
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-5">
          <FadeIn className="lg:col-span-3">
            <div
              data-testid="earning-table-card"
              className="card-hover h-full rounded-3xl border border-[#e2e8f0] bg-white p-7 sm:p-8"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-display text-xl font-extrabold text-[#1a237e]">
                  Referrals to Earnings
                </h3>
                <span className="font-mono-edu rounded-full bg-[#e8eaf6] px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-[#1a237e]">
                  ₹150 / referral
                </span>
              </div>
              <div className="mt-5" data-testid="earning-table">
                {TABLE_ROWS.map((row) => (
                  <div
                    key={row.referrals}
                    data-testid={`earning-row-${row.referrals.split(" ")[0]}`}
                    className={`flex items-center justify-between border-b border-[#e2e8f0] py-4 last:border-b-0 ${
                      row.highlight
                        ? "-mx-3 rounded-xl bg-[#fff3e0] px-3"
                        : ""
                    }`}
                  >
                    <span className="text-sm font-medium text-slate-600 sm:text-base">
                      {row.referrals}
                    </span>
                    <span
                      className={`font-mono-edu text-lg font-bold sm:text-xl ${
                        row.highlight ? "text-[#f57c00]" : "text-[#1a237e]"
                      }`}
                    >
                      {row.earnings}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>

          <FadeIn className="lg:col-span-2" delay={0.1}>
            <div
              data-testid="earning-calculator"
              className="flex h-full flex-col rounded-3xl bg-[#1a237e] p-7 text-white sm:p-8"
            >
              <p className="font-mono-edu text-xs font-bold uppercase tracking-[0.2em] text-[#f57c00]">
                Live Calculator
              </p>
              <p className="mt-2 text-sm text-white/70">
                Slide to see your monthly earnings
              </p>
              <div className="mt-8">
                <span
                  data-testid="earning-calculator-count"
                  className="font-mono-edu text-sm font-bold text-white/80"
                >
                  {referrals} referrals / month
                </span>
                <p
                  data-testid="earning-calculator-total"
                  className="font-display mt-1 text-4xl font-black text-[#f57c00] sm:text-5xl"
                >
                  ₹{earnings.toLocaleString("en-IN")}
                </p>
              </div>
              <input
                type="range"
                min="10"
                max="1000"
                step="10"
                value={referrals}
                data-testid="earning-calculator-slider"
                aria-label="Referrals per month"
                className="edu-range mt-8"
                style={{ "--fill": `${((referrals - 10) / 990) * 100}%` }}
                onChange={(e) => setReferrals(Number(e.target.value))}
              />
              <div className="mt-2 flex justify-between font-mono-edu text-[11px] text-white/50">
                <span>10</span>
                <span>1000</span>
              </div>
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={0.1}>
          <blockquote
            data-testid="earning-impact-quote"
            className="mt-10 flex max-w-3xl gap-4 rounded-3xl border border-[#e2e8f0] bg-[#f8fafc] p-7 sm:p-8"
          >
            <Sparkles className="h-6 w-6 shrink-0 text-[#f57c00]" />
            <p className="text-lg font-medium leading-relaxed text-[#1a237e] sm:text-xl">
              A teacher or influencer with a large student network can earn
              ₹75,000 or more per month — just by recommending an app they
              believe in.
            </p>
          </blockquote>
        </FadeIn>
      </div>
    </section>
  );
}